import React from 'react';
import { useIntl } from 'react-intl';
import { MdErrorOutline, MdInfoOutline } from 'react-icons/md';
import {
  PromoBanner,
  PROMO_BANNER_DENSITY_COMPACT,
  PROMO_BANNER_TONE_ERROR,
  PROMO_BANNER_TONE_INFO,
  SegmentedControl,
} from '@aibuild/ui';
import type { AppRunExecution, AppRunSetup } from '@api/apps';
import ApprovalModePicker from '@components/Chat/ApprovalModePicker';
import RunnerPicker from '@components/Chat/RunnerPicker';
import { SetupBar, SetupLink, SetupNote, SetupRow } from './AppRunSetupBar.styled';
import { CLOUD_RUNNER } from './runSetup.constants';
import { blockerMessage, skillLabel, type RunSetupBlocker } from './runSetup';

interface AppRunSetupBarProps {
  readonly setup: AppRunSetup;
  readonly selection: AppRunExecution;
  readonly blocker: RunSetupBlocker | null;
  readonly onSelect: (patch: Partial<AppRunExecution>) => void;
  readonly disabled: boolean;
  /** Another organization's app asks before every step, whatever the person chose for their own. */
  readonly approvalLocked?: boolean;
}

/** Where the next run executes, how much it may do unasked, and with which software. */
const AppRunSetupBar: React.FC<AppRunSetupBarProps> = ({
  setup,
  selection,
  blocker,
  onSelect,
  disabled,
  approvalLocked = false,
}) => {
  const intl = useIntl();
  const local = selection.runner !== CLOUD_RUNNER;
  const unavailable = setup.runners.find((runner) => runner.vmId === selection.runner)?.unavailableAlternatives ?? [];
  const needsRunnerSettings = blocker?.kind === 'noRunner' || blocker?.kind === 'outdated';

  return (
    <SetupBar data-testid="app-run__setup">
      <SetupRow>
        <RunnerPicker
          runnerType={selection.runner}
          localRunners={setup.runners}
          allowCloud={setup.runsOn !== 'LOCAL'}
          onPick={(runner) => onSelect({ runner })}
          disabled={disabled}
          menuDirection="down"
        />
        {local && (
          <ApprovalModePicker
            approvalMode={approvalLocked ? 'require_approval' : selection.approvalMode}
            onPick={(approvalMode) => onSelect({ approvalMode })}
            disabled={disabled || approvalLocked}
            menuDirection="down"
          />
        )}
        {setup.alternatives.length >= 2 && (
          <SegmentedControl
            options={setup.alternatives.map((skill) => ({
              id: skill.id,
              label: skillLabel(skill),
              disabled: disabled || (local && unavailable.includes(skill.id)),
            }))}
            value={selection.skill ?? ''}
            onChange={(skill) => onSelect({ skill })}
            ariaLabel={intl.formatMessage({ id: 'apps.run.setup.software', defaultMessage: 'Software' })}
            dataTestId="app-run__software"
          />
        )}
        {local && setup.required.length > 0 && (
          <SetupNote data-testid="app-run__needs">
            {intl.formatMessage(
              { id: 'apps.run.setup.needs', defaultMessage: 'Needs: {software}' },
              { software: setup.required.map(skillLabel).join(', ') },
            )}
          </SetupNote>
        )}
      </SetupRow>
      {blocker && (
        <PromoBanner
          density={PROMO_BANNER_DENSITY_COMPACT}
          tone={blocker.kind === 'noRunner' ? PROMO_BANNER_TONE_INFO : PROMO_BANNER_TONE_ERROR}
          icon={blocker.kind === 'noRunner' ? <MdInfoOutline /> : <MdErrorOutline />}
          title={
            <>
              {blockerMessage(blocker, intl)}{' '}
              {needsRunnerSettings && (
                <SetupLink to="/settings/local-runners">
                  {intl.formatMessage({ id: 'apps.run.setup.openRunners', defaultMessage: 'Open local runners' })}
                </SetupLink>
              )}
            </>
          }
          dataTestId="app-run__setup-blocker"
        />
      )}
    </SetupBar>
  );
};

export default AppRunSetupBar;
